import axios, { AxiosResponse } from 'axios'
import hash from 'object-hash'
import { identity } from 'ramda'

import { RequestModifier } from '../'
import {
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

export namespace Flow {
  export namespace KeyCharts {
    const CONTEXT_ID = 'equity:flow:key_charts'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'cash_flow_em_line'
        | 'b3investors_brazil_cash_flow'
        | 'alocacao_brasil'
        | 'msci_share_brasil'
        | 'alocacao_brasil_msci_global'
        | 'alocacao_brasil_msci_em'
        | 'hedge_funds_positioning'
        | 'fluxo_financeiro_estrangeiro_bolsa'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartPropsWrapper(config, data),
    }

    const isAbsoluteChart = (chart: ChartConfig['chart']) =>
      chart === 'fluxo_financeiro_estrangeiro_bolsa' ||
      chart === 'cash_flow_em_line' ||
      chart === 'b3investors_brazil_cash_flow'

    const isCashFlow = (chart: ChartConfig['chart']) =>
      chart === 'cash_flow_em_line' || chart === 'b3investors_brazil_cash_flow'

    const isAreaChart = (chart: ChartConfig['chart']) =>
      chart === 'hedge_funds_positioning' ||
      chart === 'fluxo_financeiro_estrangeiro_bolsa'

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(!isCashFlow(config.chart) ? { addMinMaxIndicators: true } : {}),
          ...(!isAbsoluteChart(config.chart)
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: {
            ...(!isAreaChart(config.chart) ? { xDateFormat: '%B %Y' } : {}),
            pointFormatter: magnitudePointFormatter({
              unit: !isAbsoluteChart(config.chart)
                ? '%'
                : isCashFlow(config.chart)
                ? 'bi'
                : '',
            }),
          },
        },
        {
          ...(isAreaChart(config.chart)
            ? {
                type: data.data.map((value) =>
                  value.name.toLowerCase() === 'net' ? 'line' : 'area'
                ),
              }
            : { type: Array(data.data.length).fill('spline') }),
        }
      )(config, data)
  }

  export namespace MutualFunds {
    const CONTEXT_ID = 'equity:flow:mutual_funds'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'pl_por_classe'
        | 'pl_por_classe_share'
        | 'captacao_liquida_industria'
        | 'captacao_liquida_acoes'
        | 'captacao_liquida_previdencia'
        | 'captacao_liquida_multimercados'
        | 'pl_ativos_brl'
        | 'acoes_por_classe'
        | 'weekly_equity_inflow'
        | 'accumulated_inflow'
        | 'funds_cash_exp'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'weekly_equity_inflow':
            return getMultipleLineChartProps(
              Context,
              {
                chartTitle: 'Captação dos fundos de ação (semanal)',
                tooltip: {
                  pointFormatter: magnitudePointFormatter(),
                  shared: true,
                  split: false,
                },
                yAxis: [{ id: 'primary' }, { id: 'secondary', opposite: true }],
              },
              {
                type: ['column', 'spline'],
                yAxis: ['secondary', 'primary'],
              }
            )(config, data)
          case 'accumulated_inflow':
            return getMultipleLineChartProps(Context, {
              addMinMaxIndicators: true,
              chartTitle: config.title,
              tooltip: { pointFormatter: magnitudePointFormatter() },
            })(config, data)
          case 'funds_cash_exp':
            return getMultipleLineChartProps(
              Context,
              {
                addMinMaxIndicators: true,
                chartTitle: config.title,
                convertToPercentages: Array(data.data.length).fill(true),
                tooltip: { pointFormatter: magnitudePointFormatter() },
              },
              { name: [''] }
            )(config, data)
          default:
            return getStackedSeriesChartProps(
              config,
              data,
              config.chart === 'pl_por_classe_share' ||
                config.chart === 'acoes_por_classe'
            )
        }
      },
    }

    const getStackedSeriesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      isPercentage: boolean = false
    ) =>
      getMultipleColumnChartProps(Context, {
        plotOptions: { column: { stacking: 'normal' } },
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: isPercentage ? '%' : '',
          }),
          xDateFormat: '%m/%Y',
        },
        xAxis: {
          labels: { format: '{value:%m/%Y}', rotation: -45 },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
        convertToPercentages: data.data.map(() => isPercentage),
      })(config, data)
  }

  export namespace PrevidenciaComplementar {
    const CONTEXT_ID = 'equity:flow:previdencia_complementar'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'carteira_consolidada'
        | 'carteira_consolidada_share'
        | 'carteira_consolidada_por_ativos'
        | 'carteira_consolidada_por_ativos_share'
        | 'superavit_deficit_acumulado'
        | 'spread_de_rentabilidade'
        | 'carteira_consolidada_por_tipo_plano'
        | 'carteira_consolidada_por_tipo_plano_share'
        | 'alocacao_renda_variavel_por_tipo'
        | 'carteira_efpc'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'carteira_consolidada_share':
          case 'carteira_consolidada_por_ativos_share':
          case 'spread_de_rentabilidade':
          case 'carteira_consolidada_por_tipo_plano_share':
          case 'alocacao_renda_variavel_por_tipo':
            return getStackedSeriesChartProps(config, data, true)
          default:
            return getStackedSeriesChartProps(config, data, false)
        }
      },
    }

    const getStackedSeriesChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      isPercentage: boolean = false
    ) =>
      getMultipleColumnChartProps(Context, {
        plotOptions: {
          column: {
            stacking: 'normal',
          },
        },
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: isPercentage ? '%' : '',
          }),
          xDateFormat: '%m/%Y',
        },
        xAxis: {
          labels: {
            format: '{value:%m/%Y}',
          },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
        convertToPercentages: data.data.map(() => isPercentage),
      })(config, data)
  }

  export namespace ContratosFuturos {
    const CONTEXT_ID = 'equity:flow:contratos_futuros'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      contract: string
      investor: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (
      config: ChartConfig,
      modifier: RequestModifier = identity
    ) => {
      const response = (await axios(
        modifier({
          url: '/api/charts/flow/contratos-futuros/informacao-do-contrato',
          method: 'POST',
          data: {
            contract: config.contract,
            investor: config.investor,
          },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const DEFAULT_Y_AXIS = {
      opposite: false,
      resize: { enabled: true },
      title: { text: '' },
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: `${data.title} - ${config.investor}`,
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%A, %b %e, %Y',
          },
          yAxis: [
            { ...DEFAULT_Y_AXIS, id: 'metrics', height: '60%' },
            {
              ...DEFAULT_Y_AXIS,
              id: 'net',
              height: '35%',
              top: '65%',
              offset: 0,
            },
          ],
        },
        {
          type: data.data.map((s) => (s.name === 'net' ? 'area' : 'spline')),
          yAxis: data.data.map((s) => (s.name === 'net' ? 'net' : 'metrics')),
        }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
